// src/pages/DashboardProject/ListingPagination.tsx
import React from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ListingPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const ListingPagination: React.FC<ListingPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if there are only a few
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate range around current page
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust range to always show 3 pages (if possible)
      if (currentPage <= 2) {
        endPage = Math.min(totalPages - 1, 4);
      } else if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add pages in range
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number') {
      onPageChange(page);
    }
  };

  const pages = getPageNumbers();

  return (
    <div className="d-flex justify-content-center mt-4">
      <Pagination>
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink 
            previous 
            onClick={() => onPageChange(currentPage - 1)}
          >
            <ChevronLeft size={16} />
          </PaginationLink>
        </PaginationItem>
        
        {pages.map((page, index) => (
          <PaginationItem 
            key={index}
            active={page === currentPage}
            disabled={page === '...'}
          >
            <PaginationLink
              onClick={() => handlePageClick(page)}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}
        
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink 
            next
            onClick={() => onPageChange(currentPage + 1)}
          >
            <ChevronRight size={16} />
          </PaginationLink>
        </PaginationItem>
      </Pagination>
    </div>
  );
};

export default ListingPagination;